import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, HelpCircle, Check, X, RotateCcw } from "lucide-react";

const haptic = () => navigator.vibrate?.(8);

export const Quiz = ({ world, accent, onComplete }) => {
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const start = async () => {
    setLoading(true);
    setAnswers({});
    setSubmitted(false);
    try {
      const { data } = await api.post("/alignment/quiz", { world_id: world.id });
      setQuestions(data.questions);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const pick = (qi, oi) => {
    if (submitted) return;
    haptic();
    setAnswers((a) => ({ ...a, [qi]: oi }));
  };

  const submit = async () => {
    setSubmitted(true);
    haptic();
    const correct = questions.filter((q, i) => answers[i] === q.answer).length;
    toast.success(`You scored ${correct} / ${questions.length}`);
    try {
      const { data } = await api.post("/alignment/complete", { kind: "quiz", world_id: world.id });
      onComplete?.(data);
    } catch (e) { /* non-blocking */ }
  };

  if (!questions) {
    return (
      <div data-testid="quiz-start" className="text-center py-8">
        <HelpCircle className="w-10 h-10 mx-auto mb-4" style={{ color: accent }} />
        <p className="text-slate-300 mb-6 text-sm">A gentle 3-question check to deepen your understanding of {world.name}.</p>
        <button
          data-testid="start-quiz-btn"
          onClick={start}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-slate-900 transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
          style={{ background: accent }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Begin quiz
        </button>
      </div>
    );
  }

  const allAnswered = questions.every((_, i) => answers[i] != null);

  return (
    <div data-testid="quiz-content" className="space-y-6">
      {questions.map((q, qi) => (
        <div key={qi}>
          <p className="text-sm font-medium text-slate-100 mb-3">{qi + 1}. {q.q}</p>
          <div className="grid gap-2">
            {q.options.map((opt, oi) => {
              const chosen = answers[qi] === oi;
              const isCorrect = q.answer === oi;
              let cls = "border-white/10 text-slate-300 hover:border-white/30";
              if (submitted && isCorrect) cls = "border-emerald-400/60 text-emerald-200 bg-emerald-400/10";
              else if (submitted && chosen && !isCorrect) cls = "border-rose-400/60 text-rose-200 bg-rose-400/10";
              else if (chosen) cls = "text-slate-900";
              return (
                <button
                  key={oi}
                  data-testid={`quiz-q${qi}-opt${oi}`}
                  onClick={() => pick(qi, oi)}
                  className={`flex items-center justify-between text-left rounded-xl border px-4 py-3 text-sm transition-colors duration-300 ${cls}`}
                  style={chosen && !submitted ? { background: accent, borderColor: accent } : {}}
                >
                  <span>{opt}</span>
                  {submitted && isCorrect && <Check className="w-4 h-4" />}
                  {submitted && chosen && !isCorrect && <X className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
          {submitted && q.why && <p className="text-xs text-slate-400 mt-2">{q.why}</p>}
        </div>
      ))}
      {!submitted ? (
        <button
          data-testid="submit-quiz-btn"
          onClick={submit}
          disabled={!allAnswered}
          className="rounded-xl px-6 py-3 text-sm font-medium text-slate-900 transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
          style={{ background: accent }}
        >
          Check answers
        </button>
      ) : (
        <button data-testid="retry-quiz-btn" onClick={start} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-slate-100 transition-colors duration-300">
          <RotateCcw className="w-4 h-4" /> New questions
        </button>
      )}
    </div>
  );
};
